import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { VolumeResponse } from '~/shared/types/api';
import { useAuthContext } from '~/shared/context/useAuthContext';
import {
  appendAccountsScope,
  getVolumeDataQueryScopeParts,
} from '~/shared/utils/organizationUtils';
import { useXFetch } from '~/shared/utils/useXFetch';

import { capacityBoundsFromMaxTib, type CapacityBounds } from '../../capacityFormat';
import { bytesInTib } from '../constants';

const VOLUMES_BASE_URL = '/api/v1/volumes';

/**
 * The capacity slider's ceiling: the largest `diskSize` in the tenant.
 *
 * There is no min/max aggregate on the API — `/volumes/count` only counts — but `diskSize` is a
 * server-sortable field (it backs the table's "Original Size" column), so sorting descending and
 * taking one row is an exact answer for the cost of a single request.
 *
 * Two things about this query are load-bearing:
 *
 * - It sends NO panel filters. Deriving the ceiling from filtered results is self-defeating:
 *   `hasCapacityFilter` decides "did the user filter by capacity?" by comparing the handles
 *   against the bounds, so a ceiling that shrinks to match the filtered rows makes the applied
 *   range stop reading as narrowed — and the next apply drops the filter entirely.
 * - It scopes by tenant only, not by the panel's pending Account selection, so the ceiling holds
 *   still while the user edits the panel.
 *
 * While the request is in flight the fallback ceiling stands in, which can sit above the real
 * one. That is safe: `capacityFiltered` records whether a clause is in play independently of
 * the bounds, so a ceiling that moves under an applied range cannot drop the filter.
 *
 * `enabled` gates the request on the panel actually being open. Reports mounts its side panel
 * unconditionally, so without this the page fires a `/volumes` request on every load — for a
 * page that otherwise never touches that endpoint.
 */
export function useVolumeCapacityBounds(enabled: boolean = true): CapacityBounds {
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();

  const volumeScopeKey = useMemo(
    () => getVolumeDataQueryScopeParts(accounts, activeTenantId),
    [accounts, activeTenantId]
  );

  const query = useQuery({
    queryKey: ['volume-capacity-bounds', JSON.stringify([...volumeScopeKey])],
    enabled,
    refetchOnWindowFocus: false,
    // The largest volume in a tenant barely moves, and a ceiling that changes under an open
    // panel is exactly what this hook exists to avoid — so no polling, unlike `useVolumesQuery`.
    staleTime: Infinity,
    // A failure here is cosmetic — the slider falls back to `defaultCapacityBounds` — and would
    // be deterministic rather than flaky, so don't spend the default three retries on it.
    retry: 1,
    queryFn: async () => {
      const params = new URLSearchParams({
        sortBy: JSON.stringify({ field: 'diskSize', ascending: false }),
        limit: '1',
        offset: '0',
      });
      appendAccountsScope(params, accounts, activeTenantId);

      const response = await api<VolumeResponse>(`${VOLUMES_BASE_URL}?${params.toString()}`);
      return response?.volumes?.[0]?.diskSize ?? 0;
    },
  });

  return useMemo(
    () => capacityBoundsFromMaxTib(query.data === undefined ? undefined : query.data / bytesInTib),
    [query.data]
  );
}
